import { axiosInstance } from '.'
import {
  CREATE_PROMOTIONAL_REFERRAL_CODE,
  LIST_PROMOTIONAL_REFERRAL_CODES,
  PROMOTIONAL_REFERRAL_CODE_BY_ID_V1,
} from './endpoints'
import { removeEmptyValueFromObject } from '../lib/utils/helpers'

export type PromotionalReferralRules = {
  waive_dealing_charges: true
}

export type CreatePromotionalReferralCodePayload = {
  code: string
  description?: string | null
  rules: PromotionalReferralRules
  is_active?: boolean
  valid_from?: string | null
  valid_until?: string | null
  max_redemptions?: number | null
}

/** Partial body for PATCH `/v1/promotional-referral-codes/{id}`. */
export type UpdatePromotionalReferralCodePayload = Partial<CreatePromotionalReferralCodePayload>

export type PromotionalReferralCodeListItem = {
  id: number | string
  code: string
  description?: string | null
  is_active?: boolean | null
  valid_from?: string | null
  valid_until?: string | null
  max_redemptions?: number | null
  rules?: Record<string, unknown> | null
  created_at?: string | null
  updated_at?: string | null
}

export type CampaignListPagination = {
  total: number
  limit: number
  count: number
  offset: number
}

export type GetPromotionalReferralCodesParams = {
  keyword?: string
  sortBy?: 'NEWEST_TO_OLDEST' | 'OLDEST_TO_NEWEST'
  offset: string
  limit: string
}

const defaultPagination = (): CampaignListPagination => ({
  total: 0,
  limit: 10,
  count: 0,
  offset: 0,
})

/**
 * Normalizes list API payloads (array root or common `{ codes | items | results }[]` shapes).
 */
export function normalizeCampaignListResponse(data: unknown): {
  items: PromotionalReferralCodeListItem[]
  pagination: CampaignListPagination
} {
  if (data === null || data === undefined) {
    return { items: [], pagination: defaultPagination() }
  }

  if (Array.isArray(data)) {
    const items = data as PromotionalReferralCodeListItem[]
    return {
      items,
      pagination: {
        total: items.length,
        limit: items.length || 10,
        count: items.length,
        offset: 0,
      },
    }
  }

  if (typeof data !== 'object') {
    return { items: [], pagination: defaultPagination() }
  }

  const d = data as Record<string, unknown>

  const pickArray = (): unknown[] => {
    for (const key of ['codes', 'promotional_referral_codes', 'items', 'results', 'rows', 'data']) {
      const v = d[key]
      if (Array.isArray(v)) return v
    }
    return []
  }

  const items = pickArray() as PromotionalReferralCodeListItem[]
  const p = d.pagination
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    const pg = p as Record<string, unknown>
    return {
      items,
      pagination: {
        total: Number(pg.total ?? items.length),
        limit: Number(pg.limit ?? 10),
        count: Number(pg.count ?? items.length),
        offset: Number(pg.offset ?? 0),
      },
    }
  }

  return {
    items,
    pagination: {
      total: items.length,
      limit: items.length || 10,
      count: items.length,
      offset: 0,
    },
  }
}

export const campaignServices = {
  createPromotionalReferralCode: async (data: CreatePromotionalReferralCodePayload) => {
    try {
      const response = await axiosInstance.post(CREATE_PROMOTIONAL_REFERRAL_CODE, data)
      return response
    } catch (error: any) {
      return error.response
    }
  },

  listPromotionalReferralCodes: async (params: GetPromotionalReferralCodesParams) => {
    try {
      const response = await axiosInstance.get(LIST_PROMOTIONAL_REFERRAL_CODES, {
        params: removeEmptyValueFromObject(params),
      })
      return response
    } catch (error: any) {
      return error.response
    }
  },

  updatePromotionalReferralCode: async (
    id: string | number,
    data: UpdatePromotionalReferralCodePayload,
  ) => {
    try {
      const response = await axiosInstance.patch(PROMOTIONAL_REFERRAL_CODE_BY_ID_V1(id), data)
      return response
    } catch (error: any) {
      return error.response
    }
  },

  deletePromotionalReferralCode: async (id: string | number) => {
    try {
      const response = await axiosInstance.delete(PROMOTIONAL_REFERRAL_CODE_BY_ID_V1(id))
      return response
    } catch (error: any) {
      return error.response
    }
  },
}
